import React, { useEffect, useMemo, useState } from 'react';
import { Button, Space, Spin, Typography } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Use pdfjs worker shipped with react-pdf's dependency
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url
).toString();

const { Text } = Typography;

export interface EpcPdfBoqSourceViewerProps {
  /** Local File from upload (preferred during review). */
  file?: File | null;
  /** Object URL or remote blob URL. */
  fileUrl?: string | null;
  pageNumber: number;
  onPageChange?: (page: number) => void;
  height?: number;
}

/**
 * Phase 2 side panel: digital PDF page viewer for Client BOQ review.
 * KEEP_SEPARATE from Manufacturing BOQ wizards.
 */
export const EpcPdfBoqSourceViewer: React.FC<EpcPdfBoqSourceViewerProps> = ({
  file,
  fileUrl,
  pageNumber,
  onPageChange,
  height = 480,
}) => {
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const source = useMemo(() => {
    if (file) return file;
    if (fileUrl) return fileUrl;
    return null;
  }, [file, fileUrl]);

  useEffect(() => {
    setLoading(true);
    setError(null);
  }, [source]);

  if (!source) {
    return <Text type="secondary">No PDF loaded</Text>;
  }

  const safePage = Math.min(Math.max(1, pageNumber || 1), Math.max(1, numPages || 1));

  return (
    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 8, background: '#fafafa' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <Text strong>Source PDF</Text>
        <Space>
          <Button
            size="small"
            icon={<LeftOutlined />}
            disabled={safePage <= 1}
            onClick={() => onPageChange?.(safePage - 1)}
          />
          <Text type="secondary">
            p.{safePage}
            {numPages ? ` / ${numPages}` : ''}
          </Text>
          <Button
            size="small"
            icon={<RightOutlined />}
            disabled={numPages > 0 && safePage >= numPages}
            onClick={() => onPageChange?.(safePage + 1)}
          />
        </Space>
      </Space>
      <div style={{ maxHeight: height, overflow: 'auto', textAlign: 'center', minHeight: 200 }}>
        {loading ? <Spin style={{ marginTop: 48 }} /> : null}
        {error ? <Text type="danger">{error}</Text> : null}
        <Document
          file={source}
          loading=""
          onLoadSuccess={(info) => {
            setNumPages(info.numPages);
            setLoading(false);
          }}
          onLoadError={(e) => {
            setError(e?.message || 'Failed to load PDF');
            setLoading(false);
          }}
        >
          <Page
            pageNumber={safePage}
            width={360}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      </div>
    </div>
  );
};
